import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { getAppAction } from 'store/actions/propertyActions';

import { useLocation, useNavigate } from 'react-router-dom';
import { ApplicationTable } from 'components/Tables/ApplicationTable';
import { openDetails } from 'store/actions/uiActions';
import { Button, Loading } from '@leapeasy/ui-kit';
import { failedNotif } from 'store/actions/notifActions';
import { getApp } from 'store/sagas/APIs/property';
import { useListQueryState } from 'hooks/useListQueryState';
import { usePaginatedListFetch } from 'hooks/usePaginatedListFetch';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { ApplicationListDropFilterFields } from 'components/ApplicationList/ApplicationListDropFilterFields';
import {
  ApplicationListActiveFilters,
  buildApplicationListFilterChips,
  parseApplicationListFilterChipId,
} from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  DEFAULT_APPLICATION_LIST_EXTRA_FILTERS,
  areArraysEqual,
  areApplicationCreatedDateRangeFiltersEqual,
  areDateRangesEqual,
  buildApplicationListRequestFilter,
  normalizeArrayFilter,
  normalizeApplicationCreatedDateRangeFilter,
  normalizeLeaseDateFilter,
  parseApplicationsFilters,
  serializeApplicationsFilters,
} from 'utils/applicationListQueryFilters';
import { EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER } from 'utils/applicationCreatedDateRangeFilter';
import { getBreadcrumbParentsFromState } from 'utils/breadcrumbNavigation';
import {
  APPLICATION_LIST_APP_TYPE_FILTER_OPTIONS,
  APPLICATION_STAGE_FILTER_OPTIONS,
} from 'data/constants/common_constants';
import { GlobalUXState } from 'components/GlobalUXState';
import { useClampListPagination } from 'hooks/useClampListPagination';

export const Applications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Application..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_APPLICATION_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseApplicationsFilters,
    serializeExtraFilters: serializeApplicationsFilters,
  });

  // Dropdown filter varailbles
  const [selectedStage, setSelectedStage] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedStage),
  );
  const [selectedAppType, setSelectedAppType] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDate, setSelectedLeaseDate] = useState(() =>
    normalizeLeaseDateFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedCreatedDate, setSelectedCreatedDate] = useState(() =>
    normalizeApplicationCreatedDateRangeFilter(extraFilters?.selectedCreatedDate),
  );
  const [selectedTenantCount, setSelectedTenantCount] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );
  const [selectedStageDraft, setSelectedStageDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedStage),
  );
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedAppType),
  );
  const [selectedLeaseDateDraft, setSelectedLeaseDateDraft] = useState(() =>
    normalizeLeaseDateFilter(extraFilters?.selectedLeaseDate),
  );
  const [selectedCreatedDateDraft, setSelectedCreatedDateDraft] = useState(() =>
    normalizeApplicationCreatedDateRangeFilter(extraFilters?.selectedCreatedDate),
  );
  const [selectedTenantCountDraft, setSelectedTenantCountDraft] = useState(() =>
    normalizeArrayFilter(extraFilters?.selectedTenantCount),
  );

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedStage = normalizeArrayFilter(extraFilters.selectedStage);
    const nextSelectedAppType = normalizeArrayFilter(extraFilters.selectedAppType);
    const nextSelectedLeaseDate = normalizeLeaseDateFilter(extraFilters.selectedLeaseDate);
    const nextSelectedCreatedDate = normalizeApplicationCreatedDateRangeFilter(
      extraFilters.selectedCreatedDate,
    );
    const nextSelectedTenantCount = normalizeArrayFilter(extraFilters.selectedTenantCount);
    setSelectedStage((prev) =>
      areArraysEqual(prev, nextSelectedStage) ? prev : nextSelectedStage,
    );
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDate((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedCreatedDate((prev) =>
      areApplicationCreatedDateRangeFiltersEqual(prev, nextSelectedCreatedDate)
        ? prev
        : nextSelectedCreatedDate,
    );
    setSelectedTenantCount((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
    setSelectedStageDraft((prev) =>
      areArraysEqual(prev, nextSelectedStage) ? prev : nextSelectedStage,
    );
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedLeaseDateDraft((prev) =>
      areDateRangesEqual(prev, nextSelectedLeaseDate) ? prev : nextSelectedLeaseDate,
    );
    setSelectedCreatedDateDraft((prev) =>
      areApplicationCreatedDateRangeFiltersEqual(prev, nextSelectedCreatedDate)
        ? prev
        : nextSelectedCreatedDate,
    );
    setSelectedTenantCountDraft((prev) =>
      areArraysEqual(prev, nextSelectedTenantCount) ? prev : nextSelectedTenantCount,
    );
  }, [extraFilters]);

  const applications = useSelector((state) => state.getIn(['property', 'application']));
  const applicationsJSON = applications?.toJS?.() ?? null;
  const applicationListError = applicationsJSON?.applicationError;

  const listQuery = useMemo(
    () => ({
      filter: buildApplicationListRequestFilter({
        filter,
        selectedStage,
        selectedAppType,
        selectedLeaseDate,
        selectedCreatedDate,
        selectedTenantCount,
      }),
      sortOptions,
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
    }),
    [
      filter,
      selectedStage,
      selectedAppType,
      selectedLeaseDate,
      selectedCreatedDate,
      selectedTenantCount,
      sortOptions,
      pagination.pageNumber,
      pagination.rowsPerPage,
    ],
  );

  usePaginatedListFetch({
    action: getAppAction,
    payload: listQuery,
  });

  const refetchList = useCallback(() => {
    dispatch(getAppAction(listQuery));
  }, [dispatch, listQuery]);

  const fetchFullExportDatasetTotal = useCallback(async () => {
    const response = await getApp({
      filter: buildApplicationListRequestFilter({
        filter: {
          searchText: '',
          options: {},
        },
        selectedStage: [],
        selectedAppType: [],
        selectedLeaseDate: { startDate: null, endDate: null },
        selectedCreatedDate: { ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER },
        selectedTenantCount: [],
      }),
      sortOptions,
      offset: 0,
      limit: 1,
    });
    return response?.total != null ? Number(response.total) : undefined;
  }, [sortOptions]);

  const {
    fullExportDatasetTotal,
    isLoadingFullExportTotal,
    loadFullExportDatasetTotal,
    resetFullExportDatasetTotal,
  } = useLazyExportDatasetTotal(fetchFullExportDatasetTotal);

  useEffect(() => {
    resetFullExportDatasetTotal();
  }, [sortOptions, resetFullExportDatasetTotal]);

  const onClickApplication = useCallback(
    (application) => {
      if (!application?.app_id) {
        dispatch(failedNotif('Application detail ID is missing.'));
        return;
      }

      dispatch(
        openDetails({
          type: 'application',
          data: application,
        }),
      );
      navigate(`/property/applications/${application.app_id}/detail`, {
        state: {
          fromApplicationsList: true,
          breadcrumbParents: getBreadcrumbParentsFromState(location.state),
        },
      });
    },
    [dispatch, navigate, location.state],
  );

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(applicationsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getApp({
            filter: buildApplicationListRequestFilter({
              filter,
              selectedStage,
              selectedAppType,
              selectedLeaseDate,
              selectedCreatedDate,
              selectedTenantCount,
            }),
            sortOptions,
            offset,
            limit,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export filtered applications right now.');
    }
  }, [
    applicationsJSON?.total,
    filter,
    selectedStage,
    selectedAppType,
    selectedLeaseDate,
    selectedCreatedDate,
    selectedTenantCount,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(await loadFullExportDatasetTotal()) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getApp({
            filter: buildApplicationListRequestFilter({
              filter: {
                searchText: '',
                options: {},
              },
              selectedStage: [],
              selectedAppType: [],
              selectedLeaseDate: { startDate: null, endDate: null },
              selectedCreatedDate: { ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER },
              selectedTenantCount: [],
            }),
            sortOptions,
            offset,
            limit,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all applications right now.');
    }
  }, [fullExportDatasetTotal, loadFullExportDatasetTotal, sortOptions]);

  const resetFunc = useCallback(() => {
    setSelectedStage([]);
    setSelectedAppType([]);
    setSelectedLeaseDate({
      startDate: null,
      endDate: null,
    });
    setSelectedCreatedDate({ ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER });
    setSelectedTenantCount([]);
    setSelectedStageDraft([]);
    setSelectedAppTypeDraft([]);
    setSelectedLeaseDateDraft({
      startDate: null,
      endDate: null,
    });
    setSelectedCreatedDateDraft({ ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER });
    setSelectedTenantCountDraft([]);
    setExtraFilters({ ...DEFAULT_APPLICATION_LIST_EXTRA_FILTERS });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);
  const applyFunc = useCallback(() => {
    setSelectedStage(selectedStageDraft);
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedLeaseDate(selectedLeaseDateDraft);
    setSelectedCreatedDate(selectedCreatedDateDraft);
    setSelectedTenantCount(selectedTenantCountDraft);
    setExtraFilters({
      selectedStage: selectedStageDraft,
      selectedAppType: selectedAppTypeDraft,
      selectedLeaseDate: selectedLeaseDateDraft,
      selectedCreatedDate: selectedCreatedDateDraft,
      selectedTenantCount: selectedTenantCountDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedStageDraft,
    selectedAppTypeDraft,
    selectedLeaseDateDraft,
    selectedCreatedDateDraft,
    selectedTenantCountDraft,
    setExtraFilters,
    setPagination,
  ]);

  const STAGE = APPLICATION_STAGE_FILTER_OPTIONS;
  const APPTYPE = APPLICATION_LIST_APP_TYPE_FILTER_OPTIONS;

  const listFilterChips = useMemo(
    () =>
      buildApplicationListFilterChips({
        selectedStage,
        selectedAppType,
        selectedLeaseDate,
        selectedCreatedDate,
        selectedTenantCount,
        stageOptions: STAGE,
        appTypeOptions: APPTYPE,
      }),
    [
      selectedStage,
      selectedAppType,
      selectedLeaseDate,
      selectedCreatedDate,
      selectedTenantCount,
      STAGE,
      APPTYPE,
    ],
  );

  const handleRemoveListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseApplicationListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'created') {
        const cleared = { ...EMPTY_APPLICATION_CREATED_DATE_RANGE_FILTER };
        setSelectedCreatedDate(cleared);
        setSelectedCreatedDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedCreatedDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'lease') {
        const cleared = { startDate: null, endDate: null };
        setSelectedLeaseDate(cleared);
        setSelectedLeaseDateDraft(cleared);
        setExtraFilters((prev) => ({ ...prev, selectedLeaseDate: cleared }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'stage') {
        const next = selectedStage.filter((s) => s !== parsed.value);
        setSelectedStage(next);
        setSelectedStageDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedStage: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'tenant') {
        const next = selectedTenantCount.filter((s) => s !== parsed.value);
        setSelectedTenantCount(next);
        setSelectedTenantCountDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedTenantCount: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedStage, selectedAppType, selectedTenantCount, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveListFilterChip, resetFunc]);

  const applicationTotal = Number(applicationsJSON?.total) || 0;

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: applicationTotal,
    isLoading: Boolean(applicationsJSON?.isLoading),
  });

  return (
    <DashboardLayoutContainer>
      {applicationsJSON ? (
        <React.Fragment>
          <CustomBox>
            {applicationListError && !applicationsJSON.isLoading ? (
              <Box display="flex" flexDirection="column" alignItems="center" gap="16px" mb={2}>
                <GlobalUXState variant="apiError" compact description={applicationListError} />
                <Button variant="primary" size="medium" onClick={refetchList}>
                  Retry
                </Button>
              </Box>
            ) : null}
            <ApplicationTable
              applications={applicationsJSON.data ?? []}
              loading={applicationsJSON.isLoading}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={loadFullExportDatasetTotal}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              listFilterActiveSummary={listFilterActiveSummary}
              listFilterActiveCount={listFilterChips.length}
              filter={filter}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: applicationTotal,
                totalPages: Math.ceil(applicationTotal / pagination.rowsPerPage) || 0,
              }}
              dropFilter={{
                component: (
                  <ApplicationListDropFilterFields
                    stageOptions={STAGE}
                    appTypeOptions={APPTYPE}
                    selectedStageDraft={selectedStageDraft}
                    onStageDraftChange={setSelectedStageDraft}
                    selectedAppTypeDraft={selectedAppTypeDraft}
                    onAppTypeDraftChange={setSelectedAppTypeDraft}
                    selectedLeaseDateDraft={selectedLeaseDateDraft}
                    onLeaseDateDraftChange={setSelectedLeaseDateDraft}
                    selectedCreatedDateDraft={selectedCreatedDateDraft}
                    onCreatedDateDraftChange={setSelectedCreatedDateDraft}
                    selectedTenantCountDraft={selectedTenantCountDraft}
                    onTenantCountDraftChange={setSelectedTenantCountDraft}
                  />
                ),
                resetFunc: resetFunc,
                applyFunc: applyFunc,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickApplication={onClickApplication}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} label={''} />
      )}
    </DashboardLayoutContainer>
  );
};

const CustomBox = styled.div`
  position: relative;
`;
